import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

const PAGES_AROUND = 3;

/**
 * Blätterleiste wie Views/Partials/Components/Paging.cshtml (erste, zurück, Seiten
 * rund um die aktuelle, weiter, letzte). Die Links sind echte URLs (öffnen in neuem
 * Tab geht), ein normaler Klick meldet nur shop-page (detail: Seitennummer).
 */
@customElement('svgw-shop-pager')
export class SvgwShopPager extends LitElement {
    @property({ type: Number }) page = 1;
    @property({ type: Number }) totalPages = 1;
    /** Liefert die URL zu einer Seite. */
    @property({ attribute: false }) href: (page: number) => string = () => '#';

    protected createRenderRoot(): HTMLElement {
        return this;
    }

    private go(event: MouseEvent, page: number): void {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) {
            return;
        }

        event.preventDefault();
        if (page !== this.page && page >= 1 && page <= this.totalPages) {
            this.dispatchEvent(new CustomEvent('shop-page', { detail: page, bubbles: true, composed: true }));
        }
    }

    private item(page: number, content: TemplateResult | number, disabled: boolean, active = false, ariaLabel?: string): TemplateResult {
        return html`
            <li class=${classMap({ 'page-item': true, disabled, active })}>
                <a class="page-link" href=${this.href(page)} aria-label=${ariaLabel ?? nothing}
                   aria-current=${active ? 'page' : nothing} @click=${(e: MouseEvent) => this.go(e, page)}>${content}</a>
            </li>`;
    }

    protected render(): TemplateResult | typeof nothing {
        if (this.totalPages <= 1) {
            return nothing;
        }

        const first = Math.max(1, this.page - PAGES_AROUND);
        const last = Math.min(this.totalPages, this.page + PAGES_AROUND);
        const pages = Array.from({ length: last - first + 1 }, (_, i) => first + i);

        return html`
            <nav aria-label="Pagination">
                <ul class="pagination">
                    ${this.item(1, html`<i class="fas fa-angle-double-left"></i>`, this.page <= 1, false, '1')}
                    ${this.item(this.page - 1, html`<i class="fa fa-angle-left"></i>`, this.page <= 1, false, String(this.page - 1))}
                    ${pages.map((p) => this.item(p, p, false, p === this.page))}
                    ${this.item(this.page + 1, html`<i class="fa fa-angle-right"></i>`, this.page >= this.totalPages, false, String(this.page + 1))}
                    ${this.item(this.totalPages, html`<i class="fas fa-angle-double-right"></i>`, this.page >= this.totalPages, false, String(this.totalPages))}
                </ul>
            </nav>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'svgw-shop-pager': SvgwShopPager;
    }
}
